import { useState, useMemo } from "react";
import { Plus, Edit2, Trash2, Package } from "lucide-react";
import Button from "../ui/Button";
import Pill from "../ui/Pill";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import ConfirmDialog from "../ui/ConfirmDialog";
import OfferForm from "./OfferForm";
import RevenuePerOfferChart from "./RevenuePerOfferChart";
import CSVActions from "../csv/CSVActions";
import { formatUSD, formatMonths } from "../../lib/formatters";
import { aggregateByOffer, aggregateOfferAllTime } from "../../lib/calculations";

export default function Offers({ state, dashboard }) {
  const { clients, offers, payments, settings } = state;
  const departments = settings?.departments || [];
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const stats = aggregateByOffer(clients, payments, offers);

  // Compute lifetime gross profit % per offer (revenue - COGS) / revenue
  // using the full P&L aggregator. Decoupled from aggregateByOffer so we
  // don't bloat that legacy helper.
  const profitByOffer = useMemo(() => {
    const map = new Map();
    offers.forEach((o) => {
      const pnl = aggregateOfferAllTime(o.id, state);
      map.set(o.id, pnl);
    });
    return map;
  }, [offers, state]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display font-semibold text-lg text-brand-ink dark:text-[var(--text-primary)]">Offers</h2>
        <div className="flex flex-wrap items-center gap-2">
          <CSVActions
            entity="offers"
            columns={["name", "type", "description"]}
            records={offers}
            sampleRow={{ name: "Done With You Monthly", type: "recurring", description: "Monthly retainer" }}
            onImport={(rows) => rows.forEach((r) => dashboard.addOffer(r))}
          />
          <Button onClick={() => setShowAdd(true)}><Plus size={14} /> Add offer</Button>
        </div>
      </div>

      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card">
        {offers.length === 0 ? (
          <EmptyState icon={Package} title="No offers yet" message="Create an offer to start tracking client revenue against it." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Department</TH>
                <TH>Type</TH>
                <TH className="text-right">Clients</TH>
                <TH className="text-right">Revenue</TH>
                <TH className="text-right">COGS</TH>
                <TH className="text-right">Gross Profit</TH>
                <TH className="text-right">GP %</TH>
                <TH className="text-right">Net Profit</TH>
                <TH className="text-right">Avg LTV</TH>
                <TH className="text-right">Avg lifetime</TH>
                <TH></TH>
              </TR>
            </THead>
            <tbody>
              {stats.map((o) => {
                const pnl = profitByOffer.get(o.id);
                const rev = pnl?.revenue || 0;
                const cogs = pnl?.cogs?.total || 0;
                const gp = pnl?.grossProfit || 0;
                const np = pnl?.netProfit || 0;
                const gpPct = rev > 0 ? (gp / rev) * 100 : null;
                return (
                <TR key={o.id}>
                  <TD className="font-medium">{o.name}<div className="text-xs text-brand-slate">{o.description}</div></TD>
                  <TD>
                    {o.department ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300">
                        {o.department}
                      </span>
                    ) : (
                      <span className="text-brand-slate text-xs">—</span>
                    )}
                  </TD>
                  <TD><Pill status={o.type === "recurring" ? "recurring" : "oneoff"}>{o.type}</Pill></TD>
                  <TD className="text-right">{o.clientCount}</TD>
                  <TD className="text-right">{formatUSD(rev)}</TD>
                  <TD className="text-right text-amber-700 dark:text-amber-400">{cogs > 0 ? `-${formatUSD(cogs)}` : "—"}</TD>
                  <TD className={`text-right font-medium ${gp >= 0 ? "text-brand-blue-dark dark:text-brand-blue" : "text-red-600"}`}>{formatUSD(gp)}</TD>
                  <TD className={`text-right font-medium ${gpPct == null ? "text-brand-slate" : gpPct >= 0 ? "text-brand-blue-dark dark:text-brand-blue" : "text-red-600"}`}>
                    {gpPct == null ? "—" : `${gpPct.toFixed(0)}%`}
                  </TD>
                  <TD className={`text-right font-medium ${np >= 0 ? "text-brand-blue-dark dark:text-brand-blue" : "text-red-600"}`}>{formatUSD(np)}</TD>
                  <TD className="text-right">{formatUSD(o.avgLTV)}</TD>
                  <TD className="text-right">{o.type === "recurring" ? formatMonths(o.avgLifetime) : "—"}</TD>
                  <TD>
                    <div className="flex justify-end gap-1">
                      <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate" onClick={() => setEditing(o)}>
                        <Edit2 size={13} />
                      </button>
                      <button className="p-1.5 rounded hover:bg-red-50 text-red-600" onClick={() => setConfirm({
                        title: "Delete offer",
                        message: `Delete ${o.name}? Existing payments linked to this offer will remain but show "Unknown offer".`,
                        confirmLabel: "Delete",
                        onConfirm: () => dashboard.deleteOffer(o.id),
                      })}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </TD>
                </TR>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      <RevenuePerOfferChart offerStats={stats} />

      <OfferForm open={showAdd} onClose={() => setShowAdd(false)} onSubmit={dashboard.addOffer} departments={departments} />
      <OfferForm
        open={!!editing}
        onClose={() => setEditing(null)}
        onSubmit={(data) => dashboard.updateOffer(editing.id, data)}
        initial={editing || undefined}
        departments={departments}
      />
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={confirm?.onConfirm || (() => {})}
      />
    </div>
  );
}
